import React from 'react';
import { motion } from 'framer-motion';
import { 
  FileText, 
  Download, 
  CheckCircle2, 
  Layers, 
  Settings, 
  ArrowRight, 
  ShieldCheck,
  FileDown
} from 'lucide-react';
import { IMAGES } from '@/assets/images';
import { ROUTE_PATHS, DownloadItem } from '@/lib/index';
import { productCategories, downloadItems } from '@/data/index';
import { ProductCard } from '@/components/Cards';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function Products() {
  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-20">
          <img 
            src={IMAGES.ALUMINUM_PRODUCTS_3} 
            alt="Ürünlerimiz" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-primary/80 to-primary" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
              Ürün Gruplarımız
            </h1>
            <p className="text-xl text-primary-foreground/80 max-w-2xl mx-auto">
              Mimariden sanayiye, yaşamın her alanına dokunan yüksek kaliteli alüminyum çözümleri üretiyoruz.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Category Overview */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-8">
            {productCategories.map((category) => (
              <ProductCard key={category.id} category={category} />
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Architectural Section */}
      <section id="mimari-sistemler" className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="lg:w-1/2 space-y-6">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                <Layers className="w-4 h-4 mr-2" />
                Mimari Sistemler
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                Geleceğin Yapılarını Bugün İnşa Ediyoruz
              </h2>
              <p className="text-lg text-muted-foreground">
                Mimalum mimari sistemleri, estetik tasarımı yüksek performansla birleştirir. ısı yalıtımı, ses izolasyonu ve rüzgar dayanımı konularında uluslararası standartları karşılayan geniş ürün yelpazemizle projelerinize değer katıyoruz.
              </p>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-4">
                {[
                  "Isı Yalıtımlı Doğramalar",
                  "Minimal Sürme Sistemleri",
                  "Giydirme Cepheler",
                  "Ofis Bölme Sistemleri",
                  "Güneş Kırıcılar",
                  "Işıklık Sistemleri",
                  "Sineklik Sistemleri",
                  "Pergola Sistemleri",
                  "Bioklimatik Pergola",
                  "Perde Profilleri",
                  "Mimari Detay Çözüm Profilleri"
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center text-foreground font-medium">
                    <CheckCircle2 className="w-5 h-5 text-primary mr-2" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
            <div className="lg:w-1/2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                <img 
                  src={IMAGES.MINIMAL_SURME_SISTEMLER_6_299}
                  alt="Mimari Sistemler" 
                  className="w-full aspect-4/3 object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-primary/10 mix-blend-multiply" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Industrial Section */}
      <section id="endustriyel-profiller" className="py-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row-reverse items-center gap-12">
            <div className="lg:w-1/2 space-y-6">
              <div className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">
                <Settings className="w-4 h-4 mr-2" />
                Endüstriyel Profiller
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-foreground">
                Sanayinin Kalbinde Hassas Çözümler
              </h2>
              <p className="text-lg text-muted-foreground">
                Otomotiv, enerji, savunma ve beyaz eşya gibi sektörlerin ihtiyaç duyduğu hassas toleranslı ve özel alaşımlı alüminyum profilleri modern ekstrüzyon hatlarımızda üretiyoruz.
              </p>
              <div className="grid gap-4 pt-4">
                <Card className="border-l-4 border-l-primary">
                  <CardContent className="p-4">
                    <h4 className="font-bold mb-1">Özel Alaşım Üretimi</h4>
                    <p className="text-sm text-muted-foreground">6060, 6063, 6005, 6082 ve 7000 serisi alaşımlarla yüksek mukavemetli çözümler.</p>
                  </CardContent>
                </Card>
                <Card className="border-l-4 border-l-primary">
                  <CardContent className="p-4">
                    <h4 className="font-bold mb-1">Mekanik İşlem Entegrasyonu</h4>
                    <p className="text-sm text-muted-foreground">Hassas kesim, delme, CNC işleme ve montaj hizmetleri tek çatı altında.</p>
                  </CardContent>
                </Card>
              </div>
            </div>
            <div className="lg:w-1/2">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                <img 
                  src={IMAGES.OTOMOTIV_305}
                  alt="Endüstriyel Profiller" 
                  className="w-full aspect-4/3 object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Download Center Section */}
      <section id="indirilebilirler" className="py-24 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold mb-4">İndirme Merkezi</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Ürün kataloglarımız, teknik çizimler ve kalite sertifikalarımıza buradan ulaşabilirsiniz.
            </p>
          </div>

          <Tabs defaultValue="all" className="w-full max-w-5xl mx-auto">
            <div className="flex justify-center mb-8">
              <TabsList className="bg-background border">
                <TabsTrigger value="all">Tümü</TabsTrigger>
                <TabsTrigger value="katalog">Kataloglar</TabsTrigger>
                <TabsTrigger value="teknik">Teknik Dokümanlar</TabsTrigger>
                <TabsTrigger value="sertifika">Sertifikalar</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="all" className="space-y-4">
              <DownloadList items={downloadItems} />
            </TabsContent>
            <TabsContent value="katalog" className="space-y-4">
              <DownloadList items={downloadItems.filter(i => i.category === 'katalog')} />
            </TabsContent>
            <TabsContent value="teknik" className="space-y-4">
              <DownloadList items={downloadItems.filter(i => i.category === 'teknik')} />
            </TabsContent>
            <TabsContent value="sertifika" className="space-y-4">
              <DownloadList items={downloadItems.filter(i => i.category === 'sertifika')} />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="bg-primary rounded-3xl p-8 md:p-16 text-center text-white relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none">
              <img src={IMAGES.ALUMINUM_PRODUCTS_4} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="relative z-10">
              <h2 className="text-3xl md:text-5xl font-bold mb-6">Projenize Özel Çözüm Mü Arıyorsunuz?</h2>
              <p className="text-xl text-primary-foreground/80 mb-10 max-w-3xl mx-auto">
                Mühendislik ekibimiz, tasarım aşamasından sevkiyata kadar tüm süreçte size destek olmaya hazır.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button size="lg" variant="secondary" className="font-bold group">
                  Teklif İste
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 font-bold">
                  Satış Ekibiyle Görüş
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function DownloadList({ items }: { items: DownloadItem[] }) {
  return (
    <motion.div 
      variants={staggerContainer}
      initial="initial"
      animate="animate"
      className="grid gap-4"
    >
      {items.map((item) => (
        <motion.div 
          key={item.id} 
          variants={fadeInUp}
          className="group"
        >
          <Card className="transition-all duration-300 hover:shadow-lg hover:border-primary/50 group-hover:-translate-y-1">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    {item.category === 'sertifika' ? <ShieldCheck className="w-6 h-6" /> : <FileText className="w-6 h-6" />}
                  </div>
                  <div>
                    <h4 className="font-bold text-lg leading-tight mb-1">{item.title}</h4>
                    <div className="flex items-center gap-4 text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                      <span>{item.category}</span>
                      <span>•</span>
                      <span>{item.fileSize}</span>
                      <span>•</span>
                      <span>Güncelleme: {item.updatedAt}</span>
                    </div>
                  </div>
                </div>
                <Button 
                  variant="ghost" 
                  className="w-full md:w-auto hover:bg-primary hover:text-white transition-colors group-hover:scale-105"
                >
                  <FileDown className="w-5 h-5 mr-2" />
                  İndir (PDF)
                </Button>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      ))}
      {items.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          Bu kategoride henüz belge bulunmamaktadır.
        </div>
      )}
    </motion.div>
  );
}
